import { useMemo, useState } from 'react';
import type { Pence, Uuid } from '@salonomy/types';
import { Button, Field, Modal, SegmentedControl, TextInput } from '@salonomy/ui';
import { money } from '../../lib/format.js';
import { poundsToPence } from './money.js';

/** A single serving-staff option for the tip attribution selector. */
export interface TipStaffOption {
  id: Uuid;
  name: string;
}

/** A captured tip line: a gratuity attributed to one staff member (integer pence, > 0). */
export interface CapturedTip {
  staffId: Uuid;
  amountPence: Pence;
}

/**
 * WS14 — CAPTURE a tip at the till. Tips are gratuities (100% the staff member's, NEVER commissionable),
 * and unlike a discount/voucher they take NO server call at capture: they are materialised as VAT-0
 * `tip` receipt lines when the receipt is issued. So this modal does NO money math against the server —
 * it records `[{staffId, amountPence}]` locally and the till charges the full (service + tips) amount on
 * the payment intent, then passes `tips` to issue-receipt. The receipt the server returns is the source
 * of truth for what was charged.
 *
 * The amount is a fixed £ OR a % of the basket (a convenience the customer chose at the till — NOT a
 * recognised-revenue figure). Attribution defaults to the appointment's primary staff; any payment-split
 * (serving) staff are selectable too. A tip may go to ONE staff member, or be SPLIT EVENLY across several
 * serving staff — split exactly in integer pence (the remainder pence land on the first selected staff so
 * Σ of the split === the entered amount; no pence is invented or lost).
 */
export function TipModal({
  basePence,
  staff,
  defaultStaffId,
  onCaptured,
  onClose,
}: {
  /** The basket's outstanding service amount — the base a "% of basket" tip is taken against (display only). */
  basePence: number;
  /** The serving-staff options (primary + any split staff). At least one is expected. */
  staff: TipStaffOption[];
  /** The default attribution (the appointment's primary staff). */
  defaultStaffId: Uuid | null;
  /** Called with one-or-more captured tip lines so the till records them (no server call here). */
  onCaptured: (tips: CapturedTip[]) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'fixed' | 'percent'>('fixed');
  const [amount, setAmount] = useState('');
  const [percent, setPercent] = useState('10');
  // The staff the tip is attributed to. Defaults to the primary; a single attribution is the common case.
  // Selecting more than one SPLITS the tip evenly across them.
  const initial =
    defaultStaffId && staff.some((s) => s.id === defaultStaffId) ? [defaultStaffId] : [];
  const [selected, setSelected] = useState<Uuid[]>(initial);

  // The tip total in integer pence (a fixed £, or % of the basket). NEVER a server figure — the customer's
  // chosen gratuity. % is computed off the basket base purely to fill the £ the customer agreed to.
  const tipTotalPence = useMemo(() => {
    if (mode === 'fixed') return poundsToPence(amount) ?? 0;
    const bps = Math.round((Number(percent) || 0) * 100);
    if (bps <= 0) return 0;
    return Math.round((basePence * bps) / 10000);
  }, [mode, amount, percent, basePence]);

  const amountOk = tipTotalPence > 0;
  const staffOk = selected.length > 0;
  const canAdd = amountOk && staffOk;

  function toggleStaff(id: Uuid) {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  /**
   * Split the entered tip evenly across the selected serving staff in EXACT integer pence: the base share
   * is floor(total / n) each, and the remainder pence (total − base·n) land on the first selected staff so
   * Σ === total. With a single staff this is just the whole amount. No pence is invented or lost.
   */
  function buildTips(): CapturedTip[] {
    const ids = selected;
    const n = ids.length;
    if (n === 1) return [{ staffId: ids[0]!, amountPence: tipTotalPence }];
    const base = Math.floor(tipTotalPence / n);
    const remainder = tipTotalPence - base * n;
    return ids.map((staffId, i) => ({
      staffId,
      amountPence: base + (i === 0 ? remainder : 0),
    }));
  }

  function submit() {
    if (!canAdd) return;
    onCaptured(buildTips());
    onClose();
  }

  const perStaffPreview =
    selected.length > 1 ? Math.floor(tipTotalPence / selected.length) : tipTotalPence;

  return (
    <Modal
      open
      onClose={onClose}
      title="Add tip"
      width={440}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canAdd}>
            {tipTotalPence > 0 ? `Add ${money(tipTotalPence)} tip` : 'Add tip'}
          </Button>
        </>
      }
    >
      <div className="till__line muted" style={{ marginBottom: 12 }}>
        <span>Basket</span>
        <span className="mono">{money(basePence)}</span>
      </div>

      <Field label="Tip amount">
        <SegmentedControl<'fixed' | 'percent'>
          options={[
            { value: 'fixed', label: 'Fixed £' },
            { value: 'percent', label: '% of basket' },
          ]}
          value={mode}
          onChange={setMode}
        />
      </Field>

      {mode === 'fixed' ? (
        <Field label="Amount (£)" hint="The gratuity the customer chose to add.">
          <TextInput
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            aria-label="Tip amount in pounds"
          />
        </Field>
      ) : (
        <Field
          label="Percent of basket (%)"
          hint={`= ${money(tipTotalPence)} of ${money(basePence)}.`}
        >
          <TextInput
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            placeholder="10"
            aria-label="Tip percent of basket"
          />
        </Field>
      )}

      <Field
        label="For"
        hint={
          selected.length > 1
            ? `Split evenly — ${money(perStaffPreview)} each (remainder to the first).`
            : 'Choose the serving staff this tip is for. Select more than one to split it.'
        }
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {staff.map((s) => {
            const on = selected.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                aria-pressed={on}
                aria-label={`Tip for ${s.name}`}
                className={`slm-segment__item${on ? ' slm-segment__item--on' : ''}`}
                style={{ borderRadius: 999, padding: '6px 12px' }}
                onClick={() => toggleStaff(s.id)}
              >
                {s.name}
              </button>
            );
          })}
        </div>
      </Field>

      <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 4 }}>
        Tips are 100% the staff member&rsquo;s gratuity — they&rsquo;re VAT-free, never the
        salon&rsquo;s revenue and never commissionable. The till charges the basket plus the tip
        together; the issued receipt shows each tip as its own line.
      </div>
    </Modal>
  );
}
